import { useCallback, useEffect, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import { useInView } from 'react-intersection-observer';
import { getUser } from '@frontend/auth-helpers';
import { useAppFlagStore } from '@frontend/shared';
import { pendo } from '@frontend/tracking';
import { useComponentEngagementStore } from '../../common/store/component-engagement-store';
import { TSS_IN_APP_BANNER_BETA_VARIANT_FF, TSS_IN_APP_BANNER_FF } from '../constants';
import { getInAppAwarenessBannerTrackingId } from '../tracking-ids';
import { InAppAwarenessPromoSource } from '../types';

interface UseCallIntelInAppBannerVisibilityProps {
  locationIds: string[];
  isDemo?: boolean;
  componentId: InAppAwarenessPromoSource;
  enabled: boolean;
  maxVisibilityCount?: number;
  maxInteractionCount?: number;
  showOncePerDay?: boolean;
  maxDismissedCount?: number;
  maxDaysSinceFirstSeen?: number;
  bannerFF?: string;
  betaBannerFF?: string;
  dismissExpiryDays?: number; // Number of days after which a dismissed banner can reappear
  onDismiss?: () => void;
}
export const useCallIntelInAppBannerVisibility = ({
  locationIds,
  isDemo,
  componentId,
  enabled = true,
  maxVisibilityCount = 1,
  maxInteractionCount = 0,
  showOncePerDay = false,
  maxDismissedCount = 0,
  maxDaysSinceFirstSeen,
  bannerFF = TSS_IN_APP_BANNER_FF,
  betaBannerFF = TSS_IN_APP_BANNER_BETA_VARIANT_FF,
  dismissExpiryDays = 30,
  onDismiss,
}: UseCallIntelInAppBannerVisibilityProps) => {
  const { getFeatureFlagValue } = useAppFlagStore();
  const user = getUser();

  const isBannerEnabled = getFeatureFlagValue({ locationIds, flagName: bannerFF });
  const isBetaBanner = getFeatureFlagValue({ locationIds, flagName: betaBannerFF });

  const { ref, inView } = useInView({
    triggerOnce: true,
  });

  const hasTrackedVisibilityRef = useRef(false);

  const componentTrackingId = useMemo(
    () => [componentId, user?.username, isBetaBanner ? 'beta' : ''].filter(Boolean).join(':'),
    [componentId, isBetaBanner, user?.username]
  );

  const { getEngagementData, checkSeenToday, trackInteraction, trackVisibility, trackDismiss } =
    useComponentEngagementStore();

  const { visibilityCount, interactionCount, dismissedCount, dismissedExpiry, firstSeen } = getEngagementData({
    componentId: componentTrackingId,
  });

  const hasSeenToday = showOncePerDay ? checkSeenToday({ componentId: componentTrackingId }) : false;

  const shouldShowBanner = useMemo(() => {
    if (isDemo) {
      return false;
    }
    if (!isBannerEnabled) {
      return false;
    }
    if (interactionCount > maxInteractionCount) {
      return false;
    }

    if (hasSeenToday && !hasTrackedVisibilityRef.current) {
      return false;
    }

    if (dismissedCount > maxDismissedCount) {
      return false;
    }
    if (visibilityCount > maxVisibilityCount) {
      return false;
    }
    if (dismissedExpiry && dayjs().isBefore(dayjs(dismissedExpiry))) {
      return false;
    }
    if (maxDaysSinceFirstSeen && firstSeen) {
      const dateDiff = dayjs().diff(dayjs(firstSeen), 'day');
      if (dateDiff > maxDaysSinceFirstSeen) {
        return false;
      }
    }

    return enabled;
  }, [
    isDemo,
    isBannerEnabled,
    interactionCount,
    maxInteractionCount,
    dismissedCount,
    maxDismissedCount,
    visibilityCount,
    maxVisibilityCount,
    dismissedExpiry,
    maxDaysSinceFirstSeen,
    firstSeen,
    enabled,
    hasSeenToday,
  ]);

  const handleInteraction = useCallback(() => {
    trackInteraction({
      componentId: componentTrackingId,
    });
  }, [componentTrackingId, trackInteraction]);

  const handleDismiss = useCallback(() => {
    trackDismiss({
      componentId: componentTrackingId,
      expiryDate: dismissExpiryDays ? dayjs().add(dismissExpiryDays, 'day').toISOString() : undefined,
    });
    onDismiss?.();
  }, [componentTrackingId, dismissExpiryDays, trackDismiss, onDismiss]);

  useEffect(() => {
    if (inView && shouldShowBanner) {
      hasTrackedVisibilityRef.current = true;

      trackVisibility({
        componentId: componentTrackingId,
      });

      pendo.track(
        getInAppAwarenessBannerTrackingId({
          isBeta: isBetaBanner,
          source: componentId,
          action: 'visible',
        }),
        {
          locationIds,
          componentId,
          isBeta: isBetaBanner,
        }
      );
    }
  }, [inView, isBetaBanner, trackVisibility, shouldShowBanner, componentTrackingId, componentId, locationIds]);

  return {
    handleDismiss,
    handleInteraction,
    ref,
    shouldShowBanner,
    isBetaBanner,
  };
};
